import { downloadFile, updateSearchParam } from '@fukuri-admins/utils/common';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetClientAdminsPartnerFeatureFlags } from '~/hooks';
import { fetchUsers, fetchUsersCSV } from '~/api/users';
import { sendBulkReminder } from '~/api/users/bulk_login_reminders';
import { useAuth } from '~/components/auth/AuthProvider';
import {
  ActionTableFilterParamsType,
  defaultActionTableFilterParams,
} from '~/types/table/ActionTableFilterParamsType';
import {
  ActionTableSortParamsType,
  defaultActionTableSortParams,
} from '~/types/table/ActionTableSortParamsType';
import { convertDateTime } from '~/utils/dateTime';
import { handleFilter } from '~/utils/filterings';
import { isApiIntegration } from '~/utils/isApiIntegration';
import { handleSorting } from '~/utils/sortings';
import { PendingUserPresentation } from '../presentations';

type PresentationProps = React.ComponentProps<typeof PendingUserPresentation>;
type PendingUserType = PresentationProps['filteredUsers'];

type IUsePendingUser = () => {
  state: {
    filteredUsers: PendingUserType;
    isLoading: PresentationProps['isLoading'];
    currentPage: PresentationProps['currentPage'];
    totalPage: PresentationProps['totalPage'];
    totalCount: PresentationProps['totalCount'];
    totalPendingCount: PresentationProps['totalPendingCount'];
    isBulkSendReminderModalOpen: PresentationProps['isBulkSendReminderModalOpen'];
    errMsg: PresentationProps['errMsg'];
    isErrorMessageModalOpen: PresentationProps['isErrorMessageModalOpen'];
    isCompleteModalOpen: PresentationProps['isCompleteModalOpen'];
    disableRowNavigation: PresentationProps['disableRowNavigation'];
  };
  actions: {
    handlePageChange: PresentationProps['handlePageChange'];
    handleBulkSendReminder: PresentationProps['handleBulkSendReminder'];
    handleOutputCsv: PresentationProps['handleOutputCsv'];
    handleHeaderSort: PresentationProps['handleHeaderSort'];
    setIsBulkSendReminderModalOpen: PresentationProps['setIsBulkSendReminderModalOpen'];
    setIsErrorMessageModalOpen: PresentationProps['setIsErrorMessageModalOpen'];
    setIsCompleteModalOpen: PresentationProps['setIsCompleteModalOpen'];
  };
};

/**
 * `useGetClientAdminsPartnerFeatureFlags` は suspense 前提の共通 hook のため、
 * このフックを呼び出す側（features/LegacyPendingUserList/index.tsx）が
 * Suspense 境界を張ること。
 */
export const usePendingUser: IUsePendingUser = () => {
  const threeLetterCode = getThreeLetterCode();
  const { data: partnerFeatureFlagData } = useGetClientAdminsPartnerFeatureFlags({
    threeLetterCode,
  });
  const apiIntegrationEnabled = isApiIntegration(partnerFeatureFlagData.partner_feature_flags);

  const [users, setUsers] = useState<PendingUserType>([]);
  const [filteredUsers, setFilteredUsers] = useState<PendingUserType>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { signOut } = useAuth();
  const [filterParams, setFilterParams] = useState<ActionTableFilterParamsType>({
    ...defaultActionTableFilterParams,
    filterColumn: 'email',
  });
  const [sortParams, setSortParams] = useState<ActionTableSortParamsType>(
    defaultActionTableSortParams
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = Number(searchParams.get('page') ?? 1);
  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [totalPage, setTotalPage] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalPendingCount, setTotalPendingCount] = useState<number>(0);
  const [isBulkSendReminderModalOpen, setIsBulkSendReminderModalOpen] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [isErrorMessageModalOpen, setIsErrorMessageModalOpen] = useState(false);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);

  const handleBulkSendReminder = async () => {
    try {
      await sendBulkReminder(signOut);
      setIsBulkSendReminderModalOpen(false);
      setIsCompleteModalOpen(true);
    } catch (error) {
      setIsBulkSendReminderModalOpen(false);
      setErrMsg(error.message);
      setIsErrorMessageModalOpen(true);
    }
  };

  const handleOutputCsv = async () => {
    const { data } = await fetchUsersCSV(signOut, { f: 'pending' });
    if (data instanceof Blob) {
      downloadFile(data, 'pending_users.csv');
    }
  };

  const getPendingUserData = useCallback(
    async (page?: number) => {
      const { data, headers } = await fetchUsers(signOut, {
        ...(page && { page: page.toString() }),
        f: 'pending',
      });

      if (!('users' in data)) {
        throw new Error('Unexpected data format');
      }

      const processedUsers = data.users.map((user, index) => {
        return {
          no: index + 1,
          id: user.id,
          full_name: user.full_name,
          email: user.email,
          created_at: convertDateTime(user.created_at),
          account_information_sent_at:
            user.account_information_sent_at === 0
              ? convertDateTime(user.created_at)
              : convertDateTime(user.account_information_sent_at),
        };
      });
      setUsers(processedUsers);
      setTotalPage(Number(headers['total-pages']));
      setTotalPendingCount(Number(headers['total-count']));
      setFilteredUsers(processedUsers);
      setIsLoading(false);
    },
    [signOut]
  );

  // NOTE: filterなしのユーザー数を取得
  const getUser = async () => {
    const { headers } = await fetchUsers(signOut);
    setTotalCount(Number(headers['total-count']));
  };

  useEffect(() => {
    getUser();
    getPendingUserData(initialPage > 1 ? initialPage : undefined);
  }, []);

  useEffect(() => {
    handleFilter(users, filterParams, false, setFilteredUsers, setFilterParams);
  }, [users, filterParams]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setSearchParams(updateSearchParam(searchParams, 'page', page.toString()));
    getPendingUserData(page);
  };

  const handleHeaderSort = (column: string) =>
    handleSorting(column, sortParams, filteredUsers, setSortParams, setFilteredUsers);

  return {
    state: {
      filteredUsers,
      isLoading,
      currentPage,
      totalPage,
      totalCount,
      totalPendingCount,
      isBulkSendReminderModalOpen,
      errMsg,
      isErrorMessageModalOpen,
      isCompleteModalOpen,
      disableRowNavigation: apiIntegrationEnabled,
    },
    actions: {
      handlePageChange,
      handleBulkSendReminder,
      handleOutputCsv,
      handleHeaderSort,
      setIsBulkSendReminderModalOpen,
      setIsErrorMessageModalOpen,
      setIsCompleteModalOpen,
    },
  };
};
